import { flattenEdges } from "@pathwright/utils"
import { AugmentationPanelItemProps } from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { ItemSelectorAvatar } from "@pathwright/ux/src/components/item-selector/ItemSelector"
import { useCallback, useMemo, useState } from "react"
import {
  CoverPageContributorFragmentDoc,
  useAuthorsQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useCoverPageContext } from "../CoverPageProvider"
import { COVER_PAGE_PANEL_PAGE_SIZE } from "../constants"
import { getAvatarUserFromContributor } from "../contributors/utils"
import {
  COVER_PAGE_CONTRIBUTOR_POSITION_ITEM_KEY,
  getContributorPositioningContext
} from "../positioning"
import { useCoverPageTranslate } from "../utils"
import {
  CoverPagePanelAddProps,
  CoverPagePanelSectionBlock
} from "./CoverPagePanelSection"
import {
  useCoverPagePanelDebouncedValue,
  useCoverPagePanelErrorMessage
} from "./utils"
import { useCoverPageAddLabel } from "./addLabels"

type ContributorOption = {
  id: number
  name: string
  image?: string | null
}

export function CoverPagePanelContributorsSection() {
  const { t } = useCoverPageTranslate()
  const addLabel = useCoverPageAddLabel("contributors")
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const { school } = usePathwrightContext()
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const contributors = useCoverPageContext((value) => value.contributors)
  const updateCoverPageResourceDetails = useCoverPageContext(
    (value) => value.updateCoverPageResourceDetails
  )
  const [search, setSearch] = useState("")
  const debouncedSearch = useCoverPagePanelDebouncedValue(search)
  const positioningContext = getContributorPositioningContext(resourceId)
  const authorsQuery = useAuthorsQuery({
    variables: {
      first: COVER_PAGE_PANEL_PAGE_SIZE,
      school_id: school?.id || 0,
      search: debouncedSearch || undefined,
      ids: contributors.length
        ? { nin: contributors.map((value) => String(value.id)) }
        : undefined,
      manage: true,
      positioningContext
    },
    // NOTE: ideally we skip this query until the selector is engaged.
    skip: !school?.id,
    notifyOnNetworkStatusChange: true,
    // Revalidate when the panel remounts; avoid stale lists after contributor creation elsewhere.
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first"
  })
  const handleAddContributor = useCallback(
    async (contributorId: number) => {
      return updateCoverPageResourceDetails({
        authors: Array.from(
          new Set(
            contributors
              .map((contributor) => contributor.id)
              .concat(contributorId)
          )
        )
      })
    },
    [contributors, updateCoverPageResourceDetails]
  )

  const searchableAuthors = useMemo(
    () => flattenEdges(authorsQuery.data?.context?.school?.authors || []),
    [authorsQuery.data]
  )

  const options = useMemo<ContributorOption[]>(
    () =>
      searchableAuthors.map((author) => ({
        id: author.id,
        name: author.full_name,
        image: author.image ?? null
      })),
    [searchableAuthors]
  )

  const handleLoadMore = useCallback(async () => {
    const hasMore =
      !!authorsQuery.data?.context?.school?.authors?.pageInfo?.hasNextPage
    const endCursor =
      authorsQuery.data?.context?.school?.authors?.pageInfo?.endCursor

    if (!hasMore || !endCursor) return

    await authorsQuery.fetchMore({
      variables: {
        cursor: endCursor
      }
    })
  }, [authorsQuery])

  const handleRemoveContributor = useCallback(
    async (contributorId: number) => {
      updateCoverPageResourceDetails({
        authors: contributors
          .filter((contributor) => contributor.id !== contributorId)
          .map((contributor) => contributor.id)
      })
    },
    [contributors, updateCoverPageResourceDetails]
  )

  const getItemAvatarProps = useCallback(
    (option: ContributorOption) => {
      const author = searchableAuthors.find((author) => author.id === option.id)

      if (!author) return undefined

      return { user: getAvatarUserFromContributor(author) }
    },
    [searchableAuthors]
  )

  const items = useMemo<AugmentationPanelItemProps[]>(
    () =>
      contributors.map((contributor) => ({
        data: contributor,
        itemKey: `edit-contributor-${contributor.id}`,
        leading: (
          <ItemSelectorAvatar
            user={getAvatarUserFromContributor(contributor)}
          />
        ),
        label: contributor.display_name,
        panelRoute: `contributors/${contributor.id}/`,
        controls: [
          {
            type: "edit" as const,
            relativeTo: `contributors/${contributor.id}/`
          },
          {
            type: "delete" as const,
            hashKey: "contributors",
            handler: () => handleRemoveContributor(contributor.id)
          }
        ]
      })),
    [contributors, handleRemoveContributor]
  )

  const addProps = useMemo<CoverPagePanelAddProps<ContributorOption>>(
    () => ({
      itemKey: "add-contributor",
      label: addLabel,
      select: {
        itemLabel: t("panel_sections.contributor", {
          defaultValue: "Contributor"
        }),
        icon: "avatar-circle",
        options,
        isLoading: authorsQuery.loading,
        getItemAvatarProps,
        getNewOptionData: (inputValue) => ({
          id: -1,
          name: inputValue,
          image: null
        }),
        onInputValueChange: setSearch,
        onMenuScrollToBottom: handleLoadMore,
        getErrorMessage: (error) =>
          getErrorMessage(
            error,
            t("panel_errors.add_contributor", {
              defaultValue: "Error adding Cover Page's contributor:"
            })
          ),
        navigateCreate: {
          panelRoute: "contributors/new"
        },
        onSubmit: async (item) => {
          // We only add existing Contributors here.
          // New contributors are created via the contributors/new panel route.
          if (!item.id || item.id < 0) {
            throw new Error(
              t("panel_contributors.select_existing_contributor", {
                defaultValue: "Select an existing contributor to add."
              })
            )
          }

          await handleAddContributor(item.id)
        }
      }
    }),
    [
      authorsQuery.loading,
      getErrorMessage,
      getItemAvatarProps,
      handleAddContributor,
      handleLoadMore,
      options,
      t
    ]
  )

  const sortProps = useMemo(
    () => ({
      positionedItems: contributors,
      itemFragmentDocument: CoverPageContributorFragmentDoc,
      itemKey: COVER_PAGE_CONTRIBUTOR_POSITION_ITEM_KEY,
      positioningContext,
      fragmentVariables: {
        contributorPositioningContext: positioningContext
      }
    }),
    [contributors, positioningContext]
  )

  return (
    <CoverPagePanelSectionBlock
      heading={t("panel_sections.contributors", {
        defaultValue: "Contributors"
      })}
      headingLinkProps={{ relativeTo: "." }}
      items={items}
      addProps={addProps}
      sortProps={sortProps}
      sectionKey="contributors"
    />
  )
}
